import { RecaptchaVerifier } from "firebase/auth"
import { LogDebug, LogError, LogWarning } from "../../helpers/logger"

let recaptchaSingleton = null
let recaptchaConsumerCount = 0
let clearTimeoutId = null
let recaptchaRenderPromise = null
const RECAPTCHA_CONTAINER_ID = "recaptcha-container"
const RECAPTCHA_FORCE_ERROR_KEY = "debug:forceRecaptchaError"
const RECAPTCHA_LIFECYCLE_DEBUG_KEY = "debug:recaptchaLifecycle"

const canUseDom = () =>
  typeof document !== "undefined" && !!document.body

const shouldLogLifecycle = () => {
  if (process.env.NODE_ENV === "production") return false
  if (typeof window === "undefined") return false

  return window.localStorage.getItem(RECAPTCHA_LIFECYCLE_DEBUG_KEY) === "1"
}

const logLifecycle = (message) => {
  if (!shouldLogLifecycle()) return

  LogDebug(`[reCAPTCHA] ${message}`, {
    consumerCount: recaptchaConsumerCount,
    hasVerifier: !!recaptchaSingleton,
    hasPendingClear: clearTimeoutId !== null,
  })
}

const clearPendingRemoval = () => {
  if (clearTimeoutId !== null) {
    clearTimeout(clearTimeoutId)
    clearTimeoutId = null
    logLifecycle("Canceled pending clear")
  }
}

// This schedules the recaptcha to be cleared asynchronously, waiting for 0 milliseconds gives us a 
// chance for any immediate reacquisitions to occur before it is actually cleared.
const scheduleClearIfUnused = () => {
  clearPendingRemoval()

  clearTimeoutId = setTimeout(() => {
    clearTimeoutId = null

    if (recaptchaConsumerCount === 0) {
      logLifecycle("Running deferred clear")
      clearRecaptcha()
    }
  }, 0)//0 defers the clear to the next event loop tick
  logLifecycle("Scheduled deferred clear")
}

const isRecaptchaErrorInjectionEnabled = () => {
  if (process.env.NODE_ENV === "production") return false
  if (typeof window === "undefined") return false

  return window.localStorage.getItem(RECAPTCHA_FORCE_ERROR_KEY) === "1"
}

export function getRecaptcha(auth) {
  if (!auth) return null
  if (!canUseDom()) return null

  if (isRecaptchaErrorInjectionEnabled()) {
    throw new Error("FORCED_RECAPTCHA_ERROR")
  }

  if (!document.getElementById(RECAPTCHA_CONTAINER_ID)) {
    const div = document.createElement("div")
    div.id = RECAPTCHA_CONTAINER_ID
    document.body.appendChild(div)
  }

  if (!recaptchaSingleton) {
    recaptchaSingleton = new RecaptchaVerifier(
      RECAPTCHA_CONTAINER_ID,
      { size: "invisible" },
      auth
    )

    const renderingVerifier = recaptchaSingleton

    recaptchaRenderPromise = renderingVerifier
      .render()
      .catch((error) => {
        LogError("Failed to render reCAPTCHA verifier:", error)

        // Only clear if this failing render still belongs to the active singleton.
        if (recaptchaSingleton === renderingVerifier) {
          clearRecaptcha()
        } else {
          LogWarning("Ignoring stale reCAPTCHA render failure")
        }

        throw error
      })
  }

  return recaptchaSingleton
}

export async function waitForRecaptchaReady(auth, timeoutMs = 30000) {
  const verifier = getRecaptcha(auth)
  if (!verifier) {
    const error = new Error("RECAPTCHA_UNAVAILABLE")
    error.code = "auth/recaptcha-unavailable"
    throw error
  }

  if (!recaptchaRenderPromise) {
    return verifier
  }

  let timeoutId = null
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const timeoutError = new Error("RECAPTCHA_RENDER_TIMEOUT")
      timeoutError.code = "auth/recaptcha-render-timeout"
      reject(timeoutError)
    }, timeoutMs)
  })

  try {
    await Promise.race([recaptchaRenderPromise, timeoutPromise])
    return recaptchaSingleton || verifier
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}

export function acquireRecaptcha(auth) {
  clearPendingRemoval()
  try {
    const verifier = getRecaptcha(auth)

    if (verifier) {
      recaptchaConsumerCount += 1
      logLifecycle("Acquired verifier")
    }

    return verifier
  } catch (error) {
    // Restore deferred cleanup if acquire fails after pending clear was canceled.
    if (recaptchaConsumerCount === 0) {
      scheduleClearIfUnused()
      logLifecycle("Acquire failed; re-scheduled clear")
    }

    throw error
  }
}

export function releaseRecaptcha() {
  if (recaptchaConsumerCount > 0) {
    recaptchaConsumerCount -= 1
    logLifecycle("Released verifier")
  }

  if (recaptchaConsumerCount === 0) {
    scheduleClearIfUnused()
  }
}

export function setRecaptchaErrorInjection(enabled) {
  if (typeof window === "undefined") return

  if (enabled) {
    window.localStorage.setItem(RECAPTCHA_FORCE_ERROR_KEY, "1")
    return
  }

  window.localStorage.removeItem(RECAPTCHA_FORCE_ERROR_KEY)
}

export function setRecaptchaLifecycleDebug(enabled) {
  if (typeof window === "undefined") return

  if (enabled) {
    window.localStorage.setItem(RECAPTCHA_LIFECYCLE_DEBUG_KEY, "1")
    return
  }

  window.localStorage.removeItem(RECAPTCHA_LIFECYCLE_DEBUG_KEY)
}

export function clearRecaptcha() {
  clearPendingRemoval()
  recaptchaConsumerCount = 0
  recaptchaRenderPromise = null

  if (recaptchaSingleton) {
    try {
      recaptchaSingleton.clear()
    } catch (error) {
      LogWarning("Error clearing reCAPTCHA:", error)
    }
    recaptchaSingleton = null
  }

  if (canUseDom()) {
    const container = document.getElementById(RECAPTCHA_CONTAINER_ID)
    if (container) {
      container.remove()
    }
  }

  logLifecycle("Cleared verifier and container")
}